import { atom, getDefaultStore, useAtom } from "jotai";
import { useCallback } from "react";

// A page or request the role of the user has no access to ( 403 )
export interface AccessDeniedEntry {
  kind: "page" | "request";
  page: string;
  time: string;
  method?: string;
  url?: string;
  params?: unknown;
  status?: number;
  errorId?: string;
  errorMessage?: string;
}

const accessDeniedAtom = atom<AccessDeniedEntry[]>([]);

// Requests closed in this session are not shown again, so a polled endpoint does not keep reopening the modal
const dismissedRequests = new Set<string>();

const entryKey = (entry: Omit<AccessDeniedEntry, "time">) =>
  entry.kind === "page" ? `page ${entry.page}` : `${entry.method} ${entry.url}`;

// Called outside React too ( axios ), so it writes to the default store
export const reportAccessDenied = (entry: Omit<AccessDeniedEntry, "time">) => {
  const key = entryKey(entry);
  if (entry.kind === "request" && dismissedRequests.has(key)) return;

  const store = getDefaultStore();
  const entries = store.get(accessDeniedAtom);
  if (entries.some((current) => entryKey(current) === key)) return;

  store.set(accessDeniedAtom, [
    ...entries,
    { ...entry, time: new Date().toISOString() },
  ]);
};

export const useAccessDenied = () => {
  const [entries, setEntries] = useAtom(accessDeniedAtom);

  const dismiss = useCallback(() => {
    entries
      .filter((entry) => entry.kind === "request")
      .forEach((entry) => dismissedRequests.add(entryKey(entry)));
    setEntries([]);
  }, [entries, setEntries]);

  return { entries, dismiss };
};
